import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, PenLine } from "lucide-react";
import { insights, insightCategories } from "../data/insights";
import { useSEO } from "../lib/seo";
import { CTABanner } from "../components/Footer";
import { PageHero } from "../components/Sections";
import { FadeUp } from "../components/motion";
import { useLang } from "../i18n";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const CategoryFilter = ({ active, onChange }) => {
  const { t } = useLang();
  return (
  <div
    role="tablist"
    aria-label="Filter articles by category"
    data-testid="insights-category-filter"
    className="insights-tabs flex flex-wrap gap-x-7 gap-y-3 pb-10"
  >
    {insightCategories.map((c) => {
      const on = c === active;
      const count = c === "All" ? insights.length : insights.filter((i) => i.category === c).length;
      return (
        <button
          key={c}
          type="button"
          role="tab"
          aria-selected={on}
          data-testid={`insights-filter-${slugify(c)}`}
          onClick={() => onChange(c)}
          className={`insights-tab ${on ? "is-active" : ""}`}
        >
          {t(c)} <span className={on ? "text-ink/60" : "text-white/30"}>{count}</span>
        </button>
      );
    })}
  </div>
  );
};

const ArticleRow = ({ a }) => {
  const { t, href } = useLang();
  return (
  <Link
    to={href(`/insights/${a.slug}`)}
    data-testid={`insight-card-${a.slug}`}
    className="insight-row group grid grid-cols-1 gap-4 border-t px-0 py-8 transition-all duration-400 sm:grid-cols-[104px_minmax(0,1fr)_24px] sm:items-start sm:gap-7"
  >
    <div className="insight-row-meta">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-gold">{t(a.date)}</p>
      <p className="mt-1 font-mono text-xs text-white/40">{t(a.readTime)}</p>
    </div>
    <div className="insight-row-copy">
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">{t(a.category)}</p>
      <h2 className="mt-2 font-display text-2xl font-semibold text-white transition-colors duration-300 group-hover:text-gold sm:text-3xl">
        {t(a.title)}
      </h2>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-mist sm:text-base">{t(a.excerpt)}</p>
      {a.author?.name && <p
        data-testid={`insight-byline-${a.slug}`}
        className="mt-4 flex items-center gap-2 text-xs text-white/50"
      >
        <PenLine className="h-3.5 w-3.5 text-gold/70" />
        <span className="text-white/80">{a.author.name}</span>
        <span aria-hidden="true">·</span>
        <span>{t(a.author.role)}</span>
      </p>}
    </div>
    <ArrowUpRight className="insight-row-arrow hidden h-5 w-5 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 sm:block sm:justify-self-end" />
  </Link>
  );
};

export default function Insights() {
  const { t, href } = useLang();
  useSEO(
    t("Insights — YUXEL"),
    t("Perspectives on business transformation: positioning, commercial systems, direct demand and AI systemization from the YUXEL team.")
  );
  const [params, setParams] = useSearchParams();
  const raw = params.get("category");
  const active = insightCategories.includes(raw) ? raw : "All";
  const list = active === "All" ? insights : insights.filter((i) => i.category === active);
  const setCategory = (c) => setParams(c === "All" ? {} : { category: c }, { replace: true });

  return (
    <>
      <PageHero
        eyebrow={t("YUXEL Intelligence")}
        title={t("Thinking that comes before action.")}
        sub={t("Notes on strategy, brand, sales, human behaviour and AI, shaped by real business problems.")}
        img="/media/concept/red-vision.webp"
        alt={t("Strategic focus and intelligence")}
      />
      <section className="creative-insights-list mx-auto max-w-7xl px-5 pb-24 sm:px-8 sm:pb-32" aria-label="Articles">
        <CategoryFilter active={active} onChange={setCategory} />
        <div className="border-b border-white/10" data-testid="insights-list">
          <AnimatePresence mode="popLayout" initial={false}>
            {list.filter(a => a.sections?.some(s => s.p?.length)).map((a, i) => (
              <motion.div
                key={a.slug}
                layout
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.2), ease: [0.22, 1, 0.36, 1] }}
              >
                <FadeUp>
                  <ArticleRow a={a} />
                </FadeUp>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </section>
      <CTABanner
        title="Prefer a conversation to an article?"
        sub="Book a consultation — the thinking above, applied to your business."
        testid="insights-cta"
      />
    </>
  );
}
